/**
 * The router's not-found page, wired as `notFoundComponent` on __root.
 *
 * Sibling of RootErrorBoundary and for the same reason: before this no route
 * defined one, so any URL the route tree does not know (a typo, a stale
 * bookmark, the prototype-era `/home`) fell through to TanStack Router's
 * default `<p>Not Found</p>` - bare text, outside the app shell, no way back.
 *
 * Root-level on purpose: the root route renders `<Outlet />` inside `<Layout>`
 * for every shell route, so this page keeps the sidebar and header around it,
 * and a member can simply carry on. Routes with their own notFoundComponent
 * (assets/$assetId) keep it - TanStack resolves the nearest one first.
 */
import { Link, useRouter } from '@tanstack/react-router';
import { ChevronLeft, Compass } from 'lucide-react';

export function RootNotFound() {
  const router = useRouter();
  const canGoBack = typeof window !== 'undefined' && window.history.length > 1;

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
        <Compass className="h-7 w-7 text-muted-foreground" />
      </div>
      <h1 className="mb-2 text-xl font-semibold text-foreground">Page not found</h1>
      <p className="mb-6 max-w-sm text-sm text-muted-foreground">
        The address may be wrong, or this page has moved.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
        <Link
          to="/"
          className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl bg-foreground px-4 text-sm font-medium text-background transition-opacity hover:opacity-90"
        >
          Go to Home
        </Link>
        {canGoBack && (
          <button
            type="button"
            onClick={() => router.history.back()}
            className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            <ChevronLeft className="h-4 w-4" />
            Go back
          </button>
        )}
      </div>
    </div>
  );
}
